import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "relative" }
const _hoisted_3 = { class: "relative" }
const _hoisted_4 = ["value"]
const _hoisted_5 = {
  key: 0,
  class: "absolute inset-y-0 right-0 pr-12 flex items-center pointer-events-none"
}
const _hoisted_6 = {
  key: 1,
  class: "absolute z-50 w-full mt-3 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto",
  style: {"top":"100%","left":"0"}
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "flex items-center space-x-4" }
const _hoisted_9 = { class: "flex-1 min-w-0" }
const _hoisted_10 = { class: "font-semibold text-black text-base leading-tight" }
const _hoisted_11 = { class: "text-sm text-black mt-1" }
const _hoisted_12 = {
  key: 2,
  class: "absolute z-40 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-md p-4 text-sm text-black",
  style: {"top":"100%","left":"0"}
}
const _hoisted_13 = { class: "mt-2" }
const _hoisted_14 = { class: "grid grid-cols-1 md:grid-cols-2 gap-6" }
const _hoisted_15 = ["value", "disabled"]
const _hoisted_16 = ["value", "disabled"]
const _hoisted_17 = { class: "grid grid-cols-1 md:grid-cols-2 gap-6" }
const _hoisted_18 = ["value", "disabled"]
const _hoisted_19 = {
  key: 0,
  class: "mt-1 text-xs text-red-600"
}
const _hoisted_20 = ["value", "disabled"]

import { ref, watch, computed } from 'vue'
import type { AddressSuggestion, AddressAutocompleteFieldProps } from '@/types/sellerRegister'


export default /*@__PURE__*/_defineComponent({
  __name: 'AddressAutocompleteField',
  props: {
    modelValue: {},
    postalCode: {},
    city: {},
    gridClass: {}
  },
  emits: ["update:model-value", "address-selected", "update:postal-code", "update:city", "postal-validation-changed"],
  setup(__props: any, { emit: __emit }) {

const props   = __props

const emit    = __emit

// Variables locales pour chaque champ
const searchQuery   = ref('');
const streetNumber  = ref();
const streetAddress = ref('');
const postalCode    = ref('');
const city          = ref('');
const manualMode    = ref(false)

// Validation du code postal (France: 5 chiffres)
const isPostalValid = computed(() => /^\d{5}$/.test(postalCode.value))

// Synchronisation avec les props du composant parent
watch(()    => props.modelValue, (newValue) => {
  streetAddress.value = newValue || ''
}, { immediate: true })

// Synchronisation des champs d'adresse depuis le composant parent
watch(() => props.postalCode, (newValue) => {
  if (newValue) postalCode.value = newValue
}, { immediate: true })

watch(() => props.city, (newValue) => {
  if (newValue) city.value = newValue
}, { immediate: true })

const showSuggestions = ref(false)
const suggestions     = ref<AddressSuggestion[]>([])
const isLoading       = ref(false)

// Debounce pour éviter trop de requêtes
let searchTimeout: number

// Cache local pour éviter les requêtes répétées
const addressCache    = new Map<string, AddressSuggestion[]>()

// Handler pour le champ de recherche
const handleSearchInput = (event: Event) => {
  const value       = (event.target as HTMLInputElement).value
  searchQuery.value = value

  // Annuler la recherche précédente
  clearTimeout(searchTimeout)
  
  // Nouvelle recherche après 300ms d'inactivité
  searchTimeout     = setTimeout(() => {
    if (value.length >= 3) {
      searchAddresses(value)
    } else {
      suggestions.value = []
    }
  }, 300)
}

const API_BASE = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '')

const buildApiUrl = (path: string) => {
  // If a base URL is provided (production), prefix it; otherwise use relative path for dev proxy
  return API_BASE ? `${API_BASE}${path}` : path
}

const searchAddresses = async (query: string) => {
  // Vérifier le cache d'abord
  if (addressCache.has(query)) {
    suggestions.value = addressCache.get(query)!
    return
  }

  isLoading.value = true
  
  try {

    const url = buildApiUrl(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=address&components=country:fr&language=fr`)
    const response = await fetch(url)
    
    if (!response.ok) {
      throw new Error(`Erreur Google Places API: ${response.status}`)
    }
    
    const data = await response.json()
    
    if (data.status !== 'OK') {
      throw new Error(`Erreur API: ${data.status}`)
    }
    
    if (!data.predictions || data.predictions.length === 0) {
      suggestions.value = []
      // Activer la saisie manuelle si rien n'est trouvé
      manualMode.value = true
      return
    }
    
    // Transformer les prédictions Google Places
    const transformedSuggestions = await Promise.all(
      data.predictions.map(async (prediction: any) => {
        // Récupérer les détails de l'adresse via backend proxy
        const detailsUrl      = buildApiUrl(`/api/places/details?place_id=${prediction.place_id}&fields=address_components,formatted_address&language=fr`)
        const detailsResponse = await fetch(detailsUrl)
        const detailsData     = await detailsResponse.json()
        
        if (detailsData.status !== 'OK') {
          return null
        }
        
        // Extraire les composants d'adresse
        const addressComponents = detailsData.result.address_components
        let streetNumber        = ''
        let streetName          = ''
        let postalCode          = ''
        let city                = ''

        addressComponents.forEach((component: any) => {
          const types = component.types
          
          if (types.includes('street_number')) {
            streetNumber = component.long_name
          } else if (types.includes('route')) {
            streetName = component.long_name
          } else if (types.includes('locality')) {
            city = component.long_name
          } else if (types.includes('postal_code')) {
            postalCode = component.long_name
          }
        })
        
        const streetAddress = streetNumber && streetName ? `${streetNumber} ${streetName}` : streetName || prediction.description.split(',')[0]
        
        return {
          streetNumber,
          streetName,
          streetAddress,
          postalCode,
          city,
          fullAddress   : prediction.description
        }
      })
    )
    
    // Filtrer les suggestions valides
    const validSuggestions = transformedSuggestions.filter(suggestion => 
      suggestion && suggestion.streetAddress && suggestion.city
    )
    
    suggestions.value = validSuggestions
    
    // Mettre en cache
    if (addressCache.size < 100) {
      addressCache.set(query, validSuggestions)
    }
    
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Address autocomplete error:', error)
    suggestions.value = []
    // En cas d'erreur, permettre la saisie manuelle
    manualMode.value = true
  } finally {
    isLoading.value = false
  }
}

const selectAddress = (address: AddressSuggestion & { streetName: string, streetNumber: string }) => {
  // Remplir tous les champs
  // console.log(address)
  streetNumber.value  = parseInt(address.streetNumber)
  streetAddress.value = address.streetName
  postalCode.value    = address.postalCode
  city.value          = address.city
  
  // Vider le champ de recherche
  searchQuery.value   = ''
  
  // Émettre les événements individuels pour chaque champ
  emit('update:model-value', address.streetAddress)
  emit('update:postal-code', address.postalCode)
  emit('update:city', address.city)
  emit('postal-validation-changed', /^\d{5}$/.test(address.postalCode))
  emit('address-selected', address)
  
  showSuggestions.value = false
  suggestions.value = []
}

const handleBlur = () => {
  // Délai pour permettre le clic sur les suggestions
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

// Gestion des bascules de mode
const toggleManualMode = () => {
  manualMode.value = !manualMode.value
  if (!manualMode.value) {
    // Retour à la recherche: vider les champs (la sélection remplira à nouveau)
    streetAddress.value = props.modelValue || ''
    postalCode.value = props.postalCode || ''
    city.value = props.city || ''
  }
}

// Gestion des saisies manuelles avec émission vers le parent
const onStreetInput = (e: Event) => {
  const val = (e.target as HTMLInputElement).value
  streetAddress.value = val
  emit('update:model-value', val)
}

const onPostalInput = (e: Event) => {
  const raw = (e.target as HTMLInputElement).value
  // Garder uniquement les chiffres et limiter à 5
  const cleaned = raw.replace(/\D/g, '').slice(0, 5)
  postalCode.value = cleaned
  emit('update:postal-code', cleaned)
  emit('postal-validation-changed', /^\d{5}$/.test(cleaned))
}

const onCityInput = (e: Event) => {
  const val = (e.target as HTMLInputElement).value
  city.value = val
  emit('update:city', val)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[4] || (_cache[4] = _createElementVNode("label", { class: "block text-sm font-medium text-black" }, [
        _createTextVNode(" Rechercher une adresse "),
        _createElementVNode("span", { class: "text-red-500" }, "*")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("input", {
          type: "text",
          value: searchQuery.value,
          onInput: handleSearchInput,
          onFocus: _cache[0] || (_cache[0] = ($event: any) => (showSuggestions.value = true)),
          onBlur: handleBlur,
          placeholder: "Entrez votre adresse (ex: 123 rue de la Paix)",
          class: "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
        }, null, 40, _hoisted_4),
        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" }, [
          _createElementVNode("i", { class: "fas fa-search text-black" })
        ], -1)),
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[1] || (_cache[1] = [
              _createElementVNode("div", { class: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" }, null, -1)
            ])))
          : _createCommentVNode("", true),
        (showSuggestions.value && suggestions.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestions.value, (suggestion, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  onClick: ($event: any) => (selectAddress(suggestion)),
                  class: "px-5 py-4 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors hover:shadow-sm"
                }, [
                  _createElementVNode("div", _hoisted_8, [
                    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "flex-shrink-0" }, [
                      _createElementVNode("i", { class: "fas fa-map-marker-alt text-blue-500 text-lg" })
                    ], -1)),
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, _toDisplayString(suggestion.streetAddress), 1),
                      _createElementVNode("div", _hoisted_11, _toDisplayString(suggestion.postalCode) + " " + _toDisplayString(suggestion.city), 1)
                    ])
                  ])
                ], 8, _hoisted_7))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        (showSuggestions.value && !isLoading.value && suggestions.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, " Aucune adresse trouvée. Vous pouvez saisir l'adresse manuellement ci-dessous. "))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("button", {
          type: "button",
          class: "text-sm text-blue-600 hover:text-blue-700 underline",
          onClick: toggleManualMode
        }, _toDisplayString(manualMode.value ? 'Revenir à la recherche d\'adresse' : 'Saisir l\'adresse manuellement'), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_14, [
      _createElementVNode("div", null, [
        _cache[5] || (_cache[5] = _createElementVNode("label", { class: "block text-sm font-medium text-black" }, [
          _createTextVNode(" Numéro "),
          _createElementVNode("span", { class: "text-red-500" }, "*")
        ], -1)),
        _createElementVNode("input", {
          type: "number",
          value: streetNumber.value,
          onInput: onStreetInput,
          placeholder: "Numéro de rue",
          disabled: !manualMode.value,
          class: _normalizeClass([
            'w-full px-3 py-2 border rounded-lg',
            manualMode.value ? 'border-gray-300 bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'border-gray-300 bg-gray-100 cursor-not-allowed text-black'
          ])
        }, null, 42, _hoisted_15)
      ]),
      _createElementVNode("div", null, [
        _cache[6] || (_cache[6] = _createElementVNode("label", { class: "block text-sm font-medium text-black" }, [
          _createTextVNode(" Rue "),
          _createElementVNode("span", { class: "text-red-500" }, "*")
        ], -1)),
        _createElementVNode("input", {
          type: "text",
          value: streetAddress.value,
          onInput: onStreetInput,
          placeholder: "Rue",
          disabled: !manualMode.value,
          class: _normalizeClass([
            'w-full px-3 py-2 border rounded-lg',
            manualMode.value ? 'border-gray-300 bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'border-gray-300 bg-gray-100 cursor-not-allowed text-black'
          ])
        }, null, 42, _hoisted_16)
      ])
    ]),
    _createElementVNode("div", _hoisted_17, [
      _createElementVNode("div", null, [
        _cache[7] || (_cache[7] = _createElementVNode("label", { class: "block text-sm font-medium text-black" }, [
          _createTextVNode(" Code postal "),
          _createElementVNode("span", { class: "text-red-500" }, "*")
        ], -1)),
        _createElementVNode("input", {
          type: "text",
          value: postalCode.value,
          onInput: onPostalInput,
          placeholder: "Code postal",
          inputmode: "numeric",
          pattern: "[0-9]*",
          maxlength: "5",
          disabled: !manualMode.value,
          class: _normalizeClass([
            'w-full px-3 py-2 border rounded-lg',
            manualMode.value ? 'border-gray-300 bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'border-gray-300 bg-gray-100 cursor-not-allowed text-black'
          ])
        }, null, 42, _hoisted_18),
        (postalCode.value && !isPostalValid.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_19, "Code postal invalide (5 chiffres)"))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", null, [
        _cache[8] || (_cache[8] = _createElementVNode("label", { class: "block text-sm font-medium text-black" }, [
          _createTextVNode(" Ville "),
          _createElementVNode("span", { class: "text-red-500" }, "*")
        ], -1)),
        _createElementVNode("input", {
          type: "text",
          value: city.value,
          onInput: onCityInput,
          placeholder: "Ville",
          disabled: !manualMode.value,
          class: _normalizeClass([
            'w-full px-3 py-2 border rounded-lg',
            manualMode.value ? 'border-gray-300 bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'border-gray-300 bg-gray-100 cursor-not-allowed text-black'
          ])
        }, null, 42, _hoisted_20)
      ])
    ])
  ]))
}
}

})